import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from "react";
import type {
  DelineationItem,
  FlowDelineationMetadata,
  HighlightData,
  BoundingBox,
} from "../types/delineation.types";

/**
 * Append one flow's boxes into `target`, keyed by document, stamping each with
 * the flow it came from so the viewer can colour it.
 *
 * Shared by the single-flow hover and the all-flows lock: both walk the same
 * shape, and the append (rather than set) is what lets several flows stack on
 * one document. A document with no boxes contributes no key — an empty entry
 * would read as "highlighted, nothing to draw".
 */
function collectDocumentBoxes(
  target: Map<string, BoundingBox[]>,
  delineation: DelineationItem[] | undefined,
  flowStatus: number | undefined,
  flowId: string,
): void {
  for (const item of delineation ?? []) {
    const boxes = item.bounding_boxes;
    if (!item.document_url || !boxes?.length) continue;
    const stamped = boxes.map((box) => ({
      ...box,
      status: flowStatus,
      flowId,
    }));
    target.set(item.document_url, [
      ...(target.get(item.document_url) ?? []),
      ...stamped,
    ]);
  }
}

/** Stable actions — never change identity, safe to consume without re-renders */
interface FlowHoverActions {
  setHoveredFlow: (
    flowId: string | null,
    metadata?: FlowDelineationMetadata | null,
    flowStatus?: number,
  ) => void;
  setAllFlowsHighlights: (
    flows: Array<{
      id: string;
      metadata: FlowDelineationMetadata;
      status: number;
    }>,
  ) => void;
  updateFlowStatus: (flowId: string, newStatus: number) => void;
  lockCurrentHighlights: () => void;
  unlockHighlights: () => void;
  clearHover: () => void;
}

/** Reactive state — changes on every hover, only subscribe when needed */
interface FlowHoverState {
  hoveredFlowId: string | null;
  highlightData: HighlightData | null;
  lockedHighlightData: HighlightData | null;
  getHighlightsForDocument: (documentUrl: string) => BoundingBox[] | undefined;
  getAllHighlights: () => BoundingBox[];
}

const FlowHoverActionsContext = createContext<FlowHoverActions | undefined>(
  undefined,
);
const FlowHoverStateContext = createContext<FlowHoverState | undefined>(
  undefined,
);

export function FlowHoverProvider({ children }: { children: React.ReactNode }) {
  const [hoveredFlowId, setHoveredFlowId] = useState<string | null>(null);
  const [highlightData, setHighlightData] = useState<HighlightData | null>(
    null,
  );
  const [lockedHighlightData, setLockedHighlightData] =
    useState<HighlightData | null>(null);

  const setHoveredFlow = useCallback(
    (
      flowId: string | null,
      metadata?: FlowDelineationMetadata | null,
      flowStatus?: number,
    ) => {
      // A flow points at its source in one of two ways, and the row counts as
      // hovered either way: an attachment flow carries `delineation` (page +
      // boxes), a mail-body flow carries `source_quote` (the verbatim text,
      // STK-1811). Only the first produces `highlightData` — the quote path
      // reads `hoveredFlowId` and searches the rendered body itself.
      if (!flowId || (!metadata?.delineation && !metadata?.source_quote)) {
        setHoveredFlowId(null);
        setHighlightData(null);
        return;
      }

      setHoveredFlowId(flowId);

      // An EMPTY delineation array is not "no delineation": the live flow can
      // carry `[]` while the real payload sits in a staged AI suggestion that
      // `useFlowHighlightRows` reads through the projection (STK-1571). Only an
      // absent key means there is nothing to overlay.
      if (!metadata.delineation) {
        setHighlightData(null);
        return;
      }

      const documentHighlights = new Map<string, BoundingBox[]>();
      collectDocumentBoxes(
        documentHighlights,
        metadata.delineation,
        flowStatus,
        flowId,
      );

      setHighlightData({ flowId, flowStatus, documentHighlights });
    },
    [],
  );

  const getHighlightsForDocument = useCallback(
    (documentUrl: string): BoundingBox[] | undefined => {
      const dataToUse = lockedHighlightData || highlightData;
      if (!dataToUse) return undefined;
      return dataToUse.documentHighlights.get(documentUrl);
    },
    [highlightData, lockedHighlightData],
  );

  const getAllHighlights = useCallback((): BoundingBox[] => {
    const dataToUse = lockedHighlightData || highlightData;
    if (!dataToUse) return [];
    const all: BoundingBox[] = [];
    dataToUse.documentHighlights.forEach((boxes) => all.push(...boxes));
    return all;
  }, [highlightData, lockedHighlightData]);

  const clearHover = useCallback(() => {
    setHoveredFlowId(null);
    setLockedHighlightData((locked) => {
      if (!locked) setHighlightData(null);
      return locked;
    });
  }, []);

  const setAllFlowsHighlights = useCallback(
    (
      flows: Array<{
        id: string;
        metadata: FlowDelineationMetadata;
        status: number;
      }>,
    ) => {
      if (flows.length === 0) {
        setLockedHighlightData(null);
        return;
      }

      const flowKey = flows
        .map((f) => f.id)
        .sort()
        .join(",");

      setLockedHighlightData((prev) => {
        if (prev?.flowId === flowKey) return prev;

        const allDocHighlights = new Map<string, BoundingBox[]>();
        for (const flow of flows) {
          collectDocumentBoxes(
            allDocHighlights,
            flow.metadata?.delineation,
            flow.status,
            flow.id,
          );
        }

        return {
          flowId: flowKey,
          flowStatus: undefined,
          documentHighlights: allDocHighlights,
        };
      });
    },
    [],
  );

  const lockCurrentHighlights = useCallback(() => {
    if (highlightData) setLockedHighlightData(highlightData);
  }, [highlightData]);

  const unlockHighlights = useCallback(() => {
    setLockedHighlightData(null);
  }, []);

  const updateFlowStatus = useCallback((flowId: string, newStatus: number) => {
    const updateMap = (
      highlights: Map<string, BoundingBox[]>,
      targetFlowId: string,
      status: number,
    ) => {
      const updated = new Map<string, BoundingBox[]>();
      highlights.forEach((boxes, url) => {
        updated.set(
          url,
          boxes.map((box) =>
            box.flowId === targetFlowId ? { ...box, status } : box,
          ),
        );
      });
      return updated;
    };

    setLockedHighlightData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        documentHighlights: updateMap(
          prev.documentHighlights,
          flowId,
          newStatus,
        ),
      };
    });

    setHighlightData((prev) => {
      if (!prev || prev.flowId !== flowId) return prev;
      return {
        ...prev,
        flowStatus: newStatus,
        documentHighlights: updateMap(
          prev.documentHighlights,
          flowId,
          newStatus,
        ),
      };
    });
  }, []);

  // Actions context — stable references, never causes re-renders in consumers
  const actions = useMemo<FlowHoverActions>(
    () => ({
      setHoveredFlow,
      setAllFlowsHighlights,
      updateFlowStatus,
      lockCurrentHighlights,
      unlockHighlights,
      clearHover,
    }),
    [
      setHoveredFlow,
      setAllFlowsHighlights,
      updateFlowStatus,
      lockCurrentHighlights,
      unlockHighlights,
      clearHover,
    ],
  );

  // State context — changes on hover, only consumed where highlights are needed
  const state = useMemo<FlowHoverState>(
    () => ({
      hoveredFlowId,
      highlightData,
      lockedHighlightData,
      getHighlightsForDocument,
      getAllHighlights,
    }),
    [
      hoveredFlowId,
      highlightData,
      lockedHighlightData,
      getHighlightsForDocument,
      getAllHighlights,
    ],
  );

  return (
    <FlowHoverActionsContext.Provider value={actions}>
      <FlowHoverStateContext.Provider value={state}>
        {children}
      </FlowHoverStateContext.Provider>
    </FlowHoverActionsContext.Provider>
  );
}

/** Use for components that only fire hover events (FlowRow) — no re-renders on hover */
export function useFlowHoverActions(): FlowHoverActions {
  const context = useContext(FlowHoverActionsContext);
  if (!context) {
    return {
      setHoveredFlow: () => {},
      setAllFlowsHighlights: () => {},
      updateFlowStatus: () => {},
      lockCurrentHighlights: () => {},
      unlockHighlights: () => {},
      clearHover: () => {},
    };
  }
  return context;
}

/** Use for components that read hover state (useFlowHighlightRows) — re-renders on hover */
export function useFlowHoverState(): FlowHoverState {
  const context = useContext(FlowHoverStateContext);
  if (!context) {
    return {
      hoveredFlowId: null,
      highlightData: null,
      lockedHighlightData: null,
      getHighlightsForDocument: () => undefined,
      getAllHighlights: () => [],
    };
  }
  return context;
}

/** Legacy API — returns both actions + state. Prefer useFlowHoverActions / useFlowHoverState. */
export function useFlowHover() {
  const actions = useFlowHoverActions();
  const state = useFlowHoverState();
  return { ...actions, ...state };
}
